import React from 'react';
import {
    ClaimTransmissionStatus,
    DocumentViewer,
    ProviderClaimTransmission,
    SupportClaimTransmission,
    renderDate,
    Spinner,
    TransmissionStatusBadge,
} from '../../../..';
import { PayloadComparisonViewer } from '../../../payload-comparison-viewer';
import _ from 'lodash';
import ContentLoader from 'react-content-loader';
import { TimelineStatusTag } from '../timeline-status-tag';
import * as Tx from './transmission-detail.styles';
import {
    formatTransmissionResponseMessage,
    transmissionStatusToTone,
} from './transmission-detail.utils';

const LinePlaceholder = React.memo(() => (
    <ContentLoader speed={2} height={20} style={{ width: '100%' }} preserveAspectRatio="">
        <rect x="0" y="0" rx="0" ry="0" width="50%" height="20" />
    </ContentLoader>
));

interface ITransmissionEmbeddedPanelProps {
    transmission: SupportClaimTransmission | ProviderClaimTransmission;
    appType: 'provider' | 'support';
    previousTransmission?: SupportClaimTransmission | ProviderClaimTransmission;
    payer?: any;
    subscriber?: any;
    loadingPayer: boolean;
    statuses: ClaimTransmissionStatus[];
    showDiff: boolean;
    documentLoading: boolean;
    documentUrl?: { url?: string };
}

const TxFact: React.FC<{
    label: string;
    children?: React.ReactNode;
    mono?: boolean;
    emptyLabel?: string;
    chip?: boolean;
    span?: number;
}> = ({ label, children, mono, emptyLabel = '—', chip, span }) => {
    const isEmpty = children == null || children === '';

    return (
        <Tx.TxFactCell $span={span}>
            <Tx.TxFactLabel>{label}</Tx.TxFactLabel>
            {isEmpty ? (
                <Tx.TxFactMuted>{emptyLabel}</Tx.TxFactMuted>
            ) : chip ? (
                <TimelineStatusTag tone="accent" label={String(children)} dot={false} />
            ) : (
                <Tx.TxFactValue $mono={mono}>{children}</Tx.TxFactValue>
            )}
        </Tx.TxFactCell>
    );
};

const TransmissionResponseHistory: React.FC<{ statuses: ClaimTransmissionStatus[] }> = ({ statuses }) => (
    <Tx.TxResponseSection $embedded>
        <Tx.TxResponseHeader>
            <Tx.TxSectionTitle style={{ margin: 0 }}>Response History</Tx.TxSectionTitle>
            <Tx.TxResponseCount>{statuses.length} events</Tx.TxResponseCount>
        </Tx.TxResponseHeader>
        <Tx.TxResponseList>
            <Tx.TxResponseRail />
            {statuses.map((status, index) => {
                const tone = transmissionStatusToTone(status.status);
                const isLast = index === statuses.length - 1;

                return (
                    <Tx.TxResponseRow key={`${status.externalId}-${index}`} $last={isLast}>
                        <Tx.TxResponseDot $tone={tone} $first={index === 0} />
                        <Tx.TxResponseMeta>
                            {status.status ? (
                                <TransmissionStatusBadge
                                    status={status.status}
                                    messageCode={status.messageCode}
                                    size="small"
                                />
                            ) : (
                                <TimelineStatusTag tone={tone} label="Update" />
                            )}
                            <Tx.TxResponseDate>
                                {status.created ? renderDate(status.created) : ''}
                            </Tx.TxResponseDate>
                        </Tx.TxResponseMeta>
                        <Tx.TxResponseBody>
                            <Tx.TxResponseMessage>
                                {formatTransmissionResponseMessage(status)}
                            </Tx.TxResponseMessage>
                            <Tx.TxResponseMetaRow>
                                {status.messageCode && (
                                    <Tx.TxResponseMetaItem>
                                        <Tx.TxFactLabel>Code</Tx.TxFactLabel>
                                        <Tx.TxCodePill>{status.messageCode}</Tx.TxCodePill>
                                    </Tx.TxResponseMetaItem>
                                )}
                                {status.externalId && (
                                    <Tx.TxResponseMetaItem>
                                        <Tx.TxFactLabel>External ID</Tx.TxFactLabel>
                                        <span>{status.externalId}</span>
                                    </Tx.TxResponseMetaItem>
                                )}
                            </Tx.TxResponseMetaRow>
                        </Tx.TxResponseBody>
                    </Tx.TxResponseRow>
                );
            })}
        </Tx.TxResponseList>
    </Tx.TxResponseSection>
);

export const TransmissionEmbeddedPanel: React.FunctionComponent<ITransmissionEmbeddedPanelProps> = ({
    transmission,
    appType,
    previousTransmission,
    payer,
    subscriber,
    loadingPayer,
    statuses,
    showDiff,
    documentLoading,
    documentUrl,
}) => {
    const nestedClearinghousePayer = (transmission as SupportClaimTransmission).clearinghousePayer;
    const payerName = nestedClearinghousePayer?.payer?.name || payer?.name;
    const clearinghouseName = nestedClearinghousePayer?.clearinghouse?.name;
    const senderName = (transmission as SupportClaimTransmission).senderName;
    const subscriberName =
        subscriber && _.compact([subscriber.first, subscriber.middle, subscriber.last]).join(' ');

    return (
        <Tx.TxPanel $embedded>
            <Tx.TxCard $embedded>
                {loadingPayer && <Spinner />}
                <Tx.TxSectionTitle>Transmission Details</Tx.TxSectionTitle>
                <Tx.TxFactsGrid>
                        <TxFact label="ID" mono>
                            {transmission.id}
                        </TxFact>
                        <TxFact label="External ID" mono>
                            {transmission.externalId}
                        </TxFact>
                        <TxFact label="Type" chip>
                            {transmission.type}
                        </TxFact>
                        {appType === 'support' ? (
                            <>
                                <TxFact label="Clearinghouse">{clearinghouseName}</TxFact>
                                <TxFact label="Payer" span={2}>
                                    {payerName}
                                </TxFact>
                                <TxFact label="Sender" emptyLabel="Pending payer response">
                                    {senderName}
                                </TxFact>
                                <TxFact label="Payer ICN" emptyLabel="Pending payer response">
                                    {transmission.payerIcn}
                                </TxFact>
                            </>
                        ) : (
                            <>
                                <TxFact label="Payer" span={2}>
                                    {payerName}
                                </TxFact>
                                <TxFact label="Subscriber" emptyLabel="Pending payer response">
                                    {subscriberName}
                                </TxFact>
                                <TxFact label="Payer ICN" emptyLabel="Pending payer response">
                                    {transmission.payerIcn}
                                </TxFact>
                            </>
                        )}
                    </Tx.TxFactsGrid>
            </Tx.TxCard>

            {statuses.length > 0 && <TransmissionResponseHistory statuses={statuses} />}

            {documentLoading && (
                <Tx.TxCard $embedded>
                    <LinePlaceholder />
                </Tx.TxCard>
            )}
            {documentUrl?.url && (
                <Tx.TxCard $embedded>
                    <DocumentViewer url={documentUrl.url} />
                </Tx.TxCard>
            )}

            <Tx.TxEdiSection $embedded>
                <Tx.TxSectionTitle>EDI Payload</Tx.TxSectionTitle>
                <PayloadComparisonViewer
                    currentX12={transmission.x12 || ''}
                    previousX12={previousTransmission?.x12}
                    previousTransmissionId={previousTransmission?.id}
                    showDiff={showDiff}
                />
            </Tx.TxEdiSection>
        </Tx.TxPanel>
    );
};
